import { useCallback } from 'react';
import { createSearchParams, useSearchParams } from 'react-router';

import { useDeferredNavigate } from 'hooks/useDeferredNavigate';
import { useLatest } from 'hooks/useLatest';
import { useSearchAnalytics } from 'modules/search/hooks/useSearchAnalytics';
import { trackEvent } from 'utils/analytics/track/trackEvent';

export function useSearchSelectFacetOption(filterType: string) {
  const [searchParams] = useSearchParams();
  const searchParamsRef = useLatest<URLSearchParams>(searchParams);

  const navigate = useDeferredNavigate();

  const { getSearchTrackingData } = useSearchAnalytics();

  const select = useCallback(
    (value: string) => {
      const filterSelected = searchParamsRef.current.has(filterType, value);

      searchParamsRef.current.delete(filterType);
      searchParamsRef.current.append(filterType, value);
      document.querySelectorAll(`*[name^=${filterType}]`).forEach((option) => {
        if (option instanceof HTMLInputElement) {
          // eslint-disable-next-line no-param-reassign
          option.checked = Boolean(option.value === value);
        }
      });

      trackEvent('Changed Search Filter', {
        filter_name: `${filterType}Facet`,
        [filterSelected ? 'selected_value' : 'deselected_value']: value,
        ...getSearchTrackingData(),
      });

      const newSearchParams = createSearchParams(searchParamsRef.current);

      navigate(`?${newSearchParams}`, { replace: true });
    },
    [filterType, getSearchTrackingData, navigate, searchParamsRef],
  );

  return select;
}
